import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import type { Theme } from '@mui/material/styles';
import type { ContractTemplate } from 'global-intern-types';
import { VORLAGEN_ARTEN, VORLAGEN_GESCHLECHTER } from '../services/vorlagenZuordnung';
import { PagePathname } from '../../../routes/paths';
import { DELETE_CONTRACT_TEMPLATE, GET_CONTRACT_TEMPLATES } from '../api/contractTemplates';
import { graphqlFehlerCode, graphqlFehlerErweiterungen } from '../services/vertragsvorlageFehler';
import { listenSperreText, loeschenGesperrtText, papierkorbGesperrtText } from '../services/vorlagensperreTexte';

// RITA-1541: Die Liste frischt die Sperr-Anzeige etwa jede Minute auf (kein Live-Kanal im PRM).
export const LISTE_SPERRE_INTERVALL_MS = 60000;

// RITA-1569: Die Spalte „Aktionen“ klebt am rechten Rand des Tabellenrahmens. Reicht die Breite für alle acht
// Spalten nicht, wandern die übrigen Spalten beim seitlichen Scrollen darunter weg; Bearbeiten · Kopieren ·
// Papierkorb bleiben ohne Verschieben sichtbar. Der Hintergrund muss deckend sein, sonst scheinen die Spalten
// durch; er ist `background.default`, weil die Tabelle ohne Paper direkt auf dem Seitenhintergrund liegt (mit
// `background.paper` fiele die Spalte im Dunkelmodus als hellerer Streifen auf). Der Zeilen-Hover ist
// halbtransparent und wird deshalb als Verlauf über den deckenden Hintergrund gelegt.
//
// Das feste Zahnrad der Einstellungen (components/settings/drawer/ToggleButton.tsx: 52 px breit, 24 px vom
// rechten Fensterrand) schwebt über jeder Seite und läge beim senkrechten Scrollen über dem Papierkorb. Die
// Spalte hält rechts deshalb 64 px Luft: Seitenrand 24 px + 64 px liegt links vom Zahnrad (24 px + 52 px)
// (Abschlussreview RITA-1569, Entscheidung Julia 28.10.2026).
const ZAHNRAD_LUFT_RECHTS = 8; // theme.spacing(8) = 64 px

const aktionenSpalteSx = (theme: Theme) =>
  ({
    position: 'sticky',
    right: 0,
    zIndex: 1,
    whiteSpace: 'nowrap',
    pr: ZAHNRAD_LUFT_RECHTS,
    bgcolor: 'background.default',
    'tbody tr:hover > &': {
      backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
    },
  }) as const;

// Die Kopfzelle trägt zusätzlich die halbtransparente Kopffarbe des Themes als Verlauf über der deckenden
// Grundfarbe (Muster `stickyHeader` in theme/overrides/Table.ts); sonst endete die eingefärbte Kopfzeile vor
// „Aktionen“ (Abschlussreview RITA-1569).
const aktionenKopfSx = (theme: Theme) =>
  ({
    ...aktionenSpalteSx(theme),
    fontWeight: 'bold',
    backgroundImage: `linear-gradient(${theme.palette.background.neutral}, ${theme.palette.background.neutral})`,
  }) as const;

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function ContractTemplateList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState<ContractTemplate | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  const { data, previousData, loading, error, refetch } = useQuery(GET_CONTRACT_TEMPLATES, {
    variables: { search: debouncedSearchTerm || undefined },
    fetchPolicy: 'network-only',
    pollInterval: LISTE_SPERRE_INTERVALL_MS,
  });
  const [deleteTemplate, { loading: deleting }] = useMutation(DELETE_CONTRACT_TEMPLATE);
  const templates: ContractTemplate[] = (data ?? (loading ? previousData : undefined))?.getContractTemplates ?? [];

  useEffect(() => setPage(0), [debouncedSearchTerm, templates.length]);

  const pagedTemplates =
    rowsPerPage === -1 ? templates : templates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDelete = async () => {
    if (!templateToDelete) return;
    setDeleteError('');
    try {
      await deleteTemplate({ variables: { id: templateToDelete.id } });
      setTemplateToDelete(null);
      await refetch();
    } catch (fehler) {
      // RITA-1541: Der Server lehnt das Löschen ab, solange eine andere Person bearbeitet (auch bei
      // veralteter Anzeige). Die Liste zeigt den Grund und holt die aktuellen Sperren.
      if (graphqlFehlerCode(fehler) === 'LOCKED') {
        const { display_name: name } = graphqlFehlerErweiterungen(fehler);
        setTemplateToDelete(null);
        setDeleteError(loeschenGesperrtText(typeof name === 'string' ? name : 'jemand anderem'));
        refetch().catch(() => undefined);
        return;
      }
      setDeleteError('Die Vertragsvorlage konnte nicht gelöscht werden. Bitte versuche es erneut.');
    }
  };

  // RITA-1541: Papierkorb und Knopf je Zeile; bei fremder Sperre nur „Ansehen“ (Entwurf v2 Bild 6, Punkt G).
  const fremdGesperrt = (template: ContractTemplate) => Boolean(template.lock && !template.lock.is_mine);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Vertragsvorlagen
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verwalte wiederverwendbare Vorlagen für Verträge.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(PagePathname.vertragsvorlagenNeu)}
          sx={{ bgcolor: '#4ADE80', color: '#000', fontWeight: 'bold', '&:hover': { bgcolor: '#3CC970' } }}
        >
          Neue Vorlage
        </Button>
      </Box>

      <TextField
        fullWidth
        placeholder="Vertragsvorlagen durchsuchen..."
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteError}
        </Alert>
      )}

      {loading && templates.length === 0 && (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress aria-label="Vertragsvorlagen werden geladen" />
        </Box>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Erneut versuchen
            </Button>
          }
        >
          Die Vertragsvorlagen konnten nicht geladen werden.
        </Alert>
      )}

      {!loading && !error && templates.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
            {debouncedSearchTerm ? 'Keine passende Vertragsvorlage gefunden' : 'Noch keine Vertragsvorlagen vorhanden'}
          </Typography>
          {!debouncedSearchTerm && (
            <Button variant="contained" onClick={() => navigate(PagePathname.vertragsvorlagenNeu)}>
              Erste Vertragsvorlage erstellen
            </Button>
          )}
        </Box>
      )}

      {!error && templates.length > 0 && (
        <>
          <TableContainer>
            <Table aria-label="Vertragsvorlagen" aria-busy={loading}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  {['Art', 'Firma', 'Vertragsart', 'Für'].map((titel) => (
                    <TableCell key={titel} sx={{ fontWeight: 'bold' }}>
                      {titel}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 'bold' }}>Beschreibung</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Zuletzt geändert</TableCell>
                  <TableCell sx={aktionenKopfSx} align="right">
                    Aktionen
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedTemplates.map((template) => (
                  <TableRow key={template.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionOutlinedIcon color="action" fontSize="small" />
                        <Typography sx={{ fontWeight: 'bold' }}>{template.name}</Typography>
                      </Box>
                      {template.lock && (
                        <Typography
                          variant="caption"
                          component="div"
                          data-testid="vertragsvorlage-listen-sperre"
                          sx={{ color: 'warning.dark', mt: 0.5 }}
                        >
                          <span aria-hidden="true">🔒 </span>
                          {listenSperreText(template.lock)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {template.assignment ? VORLAGEN_ARTEN[template.assignment.kind] : 'nicht zugeordnet'}
                    </TableCell>
                    <TableCell>{template.assignment?.branch_office_name || '—'}</TableCell>
                    <TableCell>{template.assignment?.contract_type_name || '—'}</TableCell>
                    <TableCell>
                      {template.assignment ? VORLAGEN_GESCHLECHTER[template.assignment.gender] : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {template.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(template.updated_at)}</TableCell>
                    <TableCell align="right" sx={aktionenSpalteSx}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={fremdGesperrt(template) ? <VisibilityOutlinedIcon /> : <EditIcon />}
                          onClick={() =>
                            navigate(PagePathname.vertragsvorlagenBearbeiten.replace(':id', String(template.id)))
                          }
                          sx={{ textTransform: 'none' }}
                        >
                          {fremdGesperrt(template) ? 'Ansehen' : 'Bearbeiten'}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => navigate(`${PagePathname.vertragsvorlagenNeu}?kopie=${template.id}`)}
                          sx={{ textTransform: 'none' }}
                        >
                          Kopieren
                        </Button>
                        {fremdGesperrt(template) && template.lock ? (
                          <Tooltip title={papierkorbGesperrtText(template.lock)}>
                            <span>
                              <IconButton
                                size="small"
                                disabled
                                aria-label={`Vertragsvorlage ${template.name} löschen – ${papierkorbGesperrtText(template.lock)}`}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <IconButton
                            size="small"
                            aria-label={`Vertragsvorlage ${template.name} löschen`}
                            onClick={() => {
                              setDeleteError('');
                              setTemplateToDelete(template);
                            }}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={templates.length}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50, { label: 'Alle', value: -1 }]}
            labelRowsPerPage="Zeilen pro Seite"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {templates.length} Vertragsvorlagen gesamt
          </Typography>
        </>
      )}

      <Dialog open={Boolean(templateToDelete)} onClose={() => !deleting && setTemplateToDelete(null)}>
        <DialogTitle>Vertragsvorlage löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest du die Vertragsvorlage &bdquo;{templateToDelete?.name}&ldquo; wirklich löschen? Diese Aktion kann
            nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setTemplateToDelete(null)}>
            Abbrechen
          </Button>
          <Button disabled={deleting} onClick={handleDelete} color="error" variant="contained">
            {deleting ? 'Wird gelöscht …' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
